/**
 * Exact-first, unique-only case-insensitive serial matching for warehouse scan lists.
 *
 * GS1 AI (21) serials are case-sensitive. Migrated stock sometimes stores a different casing than
 * the physical DataMatrix, so a miss gets one case-insensitive attempt. Ambiguous case-only
 * collisions return null — never guess.
 */

export function serialsMatchForScan(
  stored: string | null | undefined,
  scanned: string | null | undefined
): boolean {
  if (stored == null || scanned == null) return false;
  if (stored === scanned) return true;
  return stored.toLowerCase() === scanned.toLowerCase();
}

export function matchUniqueSerialCaseInsensitive<T>(
  items: readonly T[],
  scanned: string | null | undefined,
  getSerial: (item: T) => string | null | undefined = (item: any) => item?.serialNumber
): T | null {
  const needle = typeof scanned === 'string' ? scanned.trim() : '';
  if (!needle) return null;
  const exact = items.filter((item) => getSerial(item) === needle);
  if (exact.length === 1) return exact[0];
  if (exact.length > 1) return null;
  const lower = needle.toLowerCase();
  const loose = items.filter((item) => {
    const serial = getSerial(item);
    return typeof serial === 'string' && serial.toLowerCase() === lower;
  });
  return loose.length === 1 ? loose[0] : null;
}

/** Look up a serial-keyed map with exact-first, unique-only case-insensitive fallback. */
export function getUniqueBySerialCaseInsensitive<T>(
  bySerial: Map<string, T>,
  scanned: string | null | undefined
): T | null {
  const needle = typeof scanned === 'string' ? scanned.trim() : '';
  if (!needle) return null;
  if (bySerial.has(needle)) return bySerial.get(needle) ?? null;
  const lower = needle.toLowerCase();
  let hit: T | null = null;
  for (const [key, value] of bySerial) {
    if (key.toLowerCase() !== lower) continue;
    if (hit != null) return null;
    hit = value;
  }
  return hit;
}
